import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { Centered } from "@/components/ui/container";
import { Tab, TabPanel, Tabs, TabsList } from "@/components/ui/tabs";
import { Text } from "@/components/ui/text";
import { mutationResponse } from "@/utils/trpc";
import { PanelDetailForm } from "../components/panels/PanelDetailForm";
import { PanelOrgsTable } from "../components/panels/PanelOrgsTable";
import { PanelPanelsTable } from "../components/panels/PanelPanelsTable";
import { PanelTestsTable } from "../components/panels/PanelTestsTable";
import { Section } from "../components/Section";
import { useTabSearch } from "../hooks";

export function EditPanelPage(params: { id: number }) {
  const tabsProps = useTabSearch();

  const {
    data: panel,
    isPending,
    error,
  } = trpc.admin.panel.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (error) {
    return (
      <Centered>
        <Text isDanger>{error.message}</Text>
      </Centered>
    );
  }

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/panels" }}>Panels</Breadcrumb>
            <Breadcrumb>{panel.name}</Breadcrumb>
          </>
        }
        actions={
          <DeletePanelDialog panel={panel}>
            <Button variant="destructive">Delete</Button>
          </DeletePanelDialog>
        }
      >
        {panel.name}
      </Section.Title>
      <Tabs {...tabsProps}>
        <TabsList className="w-full justify-start">
          <Tab id="details">Details</Tab>
          <Tab id="tests">Tests</Tab>
          <Tab id="subpanels">Sub-panels</Tab>
          <Tab id="clients">Clients</Tab>
        </TabsList>
        <TabPanel id="details">
          <PanelDetailForm panel={panel} />
        </TabPanel>
        <TabPanel id="tests">
          <PanelTestsTable panel={panel} />
        </TabPanel>
        <TabPanel id="subpanels">
          <PanelPanelsTable panel={panel} />
        </TabPanel>
        <TabPanel id="clients">
          <PanelOrgsTable panel={panel} />
        </TabPanel>
      </Tabs>
    </>
  );
}

function DeletePanelDialog({
  children,
  panel,
}: {
  children: ReactNode;
  panel: { id: number };
}) {
  const [open, setOpen] = useState(false);
  const deletePanel = trpc.admin.panel.delete.useMutation();
  const navigate = useNavigate();

  const onConfirm = () => {
    deletePanel.mutate(
      panel,
      mutationResponse({
        errorTitle: "Error deleting panel",
        successMessage: "Panel deleted",
        onSuccess() {
          setOpen(false);
          navigate({ to: "/admin/panels" });
        },
      }),
    );
  };

  return (
    <ConfirmPrompt
      danger
      open={open}
      onOpenChange={setOpen}
      title="Delete panel"
      message="Are you sure you want to delete this panel?"
      onConfirm={onConfirm}
      loading={deletePanel.isPending}
    >
      {children}
    </ConfirmPrompt>
  );
}
